import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, XCircle, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { readResponse } from '@/lib/fetchJson';
import { ImportUploadZone } from './import-report/ImportUploadZone';
import { ImportModelSelect } from './import-report/ImportModelSelect';
import { ImportChatView } from './import-report/ImportChatView';

const API = process.env.REACT_APP_BACKEND_URL || '';

function formatAnalysisSummary(analysis) {
  if (!analysis || analysis.parse_error) {
    return "I had some trouble analyzing this report. Could you describe what kind of report this is?";
  }
  const visuals = analysis.visuals || [];
  const fields = analysis.detected_data_fields || [];
  const confidence = analysis.confidence_score || 0;
  let summary = `I've analyzed your report and here's what I found:\n\n`;
  summary += `**Layout:** ${analysis.page_setup?.orientation || 'portrait'} ${analysis.page_setup?.page_size || 'letter'}\n`;
  summary += `**Visuals detected:** ${visuals.length} element(s)\n`;
  if (visuals.length > 0) {
    summary += `\n**Elements:**\n`;
    visuals.forEach((v, i) => { summary += `• ${v.title || `Visual ${i + 1}`} (${v.type})\n`; });
  }
  if (fields.length > 0) {
    summary += `\n**Data fields detected:**\n`;
    fields.slice(0, 5).forEach(f => { summary += `• ${f.name} (${f.type})\n`; });
    if (fields.length > 5) summary += `  ...and ${fields.length - 5} more\n`;
  }
  summary += `\n**Confidence:** ${Math.round(confidence * 100)}%`;
  if (analysis.notes) summary += `\n\n**Notes:** ${analysis.notes}`;
  return summary;
}

export function ImportReportDialog({ open, onOpenChange }) {
  const navigate = useNavigate();

  const csrfHeader = () => {
    const m = document.cookie.match(/(?:^|; )csrf_token=([^;]+)/);
    return m ? { 'X-CSRF-Token': decodeURIComponent(m[1]) } : {};
  };

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [outputType, setOutputType] = useState('report');

  useEffect(() => {
    if (open) {
      setModelsLoading(true);
      api.getSemanticModels().then(m => {
        setModels(m || []);
        if (m?.length === 1) setSelectedModelId(m[0].model_id);
      }).catch(() => {}).finally(() => setModelsLoading(false));
    }
  }, [open]);

  const resetState = () => {
    setSessionId(null); setFileName(''); setPageCount(0); setAnalysis(null);
    setStatus('idle'); setError(null); setMessages([]); setInputMessage('');
    setUploadProgress(0); setPendingFile(null); setSelectedModelId(''); setOutputType('report');
  };

  const handleFile = (file) => {
    const name = (file.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type) && !isExcel) { toast.error('Please upload a PNG, JPEG, WEBP image, PDF, or Excel (.xlsx/.xls) file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File size must be less than 10MB'); return; }
    setFileName(file.name);
    setPendingFile(file);
    setStatus('model_select');
  };

  const pollAnalysis = async (sid) => {
    const deadline = Date.now() + 4 * 60 * 1000; // allow up to 4 minutes
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() > deadline) throw new Error('Analysis is taking longer than expected. Please try again.');
      await new Promise(r => setTimeout(r, 2000));
      const res = await fetch(`${API}/api/import/${sid}`, { credentials: 'include', headers: { ...csrfHeader() } });
      const s = await readResponse(res, 'Could not check analysis status');
      if (s.status === 'analyzed') return s;
      if (s.status === 'error') throw new Error(s.error || 'The file could not be analyzed. Please try a different file.');
    }
  };

  const proceedWithUpload = async () => {
    if (!pendingFile || !selectedModelId) return;
    setStatus('uploading'); setError(null);
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('model_id', selectedModelId);
      const progressInterval = setInterval(() => setUploadProgress(prev => Math.min(prev + 10, 90)), 200);
      const response = await fetch(`${API}/api/import/upload`, {
        method: 'POST', credentials: 'include', headers: { ...csrfHeader() }, body: formData,
      });
      clearInterval(progressInterval);
      setUploadProgress(100);
      const data = await readResponse(response, 'Upload failed');
      setSessionId(data.session_id);
      setPageCount(data.page_count || 0);
      // Analysis now runs server-side in the background (so this request isn't
      // held open long enough to hit a reverse-proxy timeout). Poll the session
      // until the result is ready. Keep the 'uploading' spinner ("Uploading and
      // analyzing...") visible throughout.
      const session = await pollAnalysis(data.session_id);
      const result = session.analysis;
      setAnalysis(result);
      setPageCount(session.page_count || data.page_count || 0);
      setStatus('analyzed');
      if (result) {
        const aiMsg = { role: 'assistant', content: formatAnalysisSummary(result), timestamp: new Date().toISOString() };
        setMessages([aiMsg]);
        const questions = result.questions || [];
        if (questions.length > 0) {
          setMessages(prev => [...prev, { role: 'assistant', content: `I have a few questions to better understand your report:\n\n${questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`, timestamp: new Date().toISOString(), isQuestion: true }]);
        }
      }
      toast.success(`Analyzed ${session.page_count || data.page_count || 1} page(s) successfully`);
    } catch (err) {
      setStatus('error'); setError(err.message); toast.error(err.message);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: inputMessage, timestamp: new Date().toISOString() }]);
    const msg = inputMessage;
    setInputMessage(''); setSending(true); setStatus('chatting');
    try {
      const response = await fetch(`${API}/api/import/${sessionId}/message`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeader() },
        body: JSON.stringify({ message: msg }),
      });
      if (!response.ok) { const err = await response.json().catch(() => null); throw new Error((err && err.detail) || 'Failed to send message'); }
      const data = await response.json().catch(() => ({}));
      if (data.analysis) setAnalysis(data.analysis);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }]);
      setStatus('analyzed');
    } catch (err) {
      toast.error(err.message);
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I encountered an error: ${err.message}`, timestamp: new Date().toISOString(), isError: true }]);
    } finally { setSending(false); }
  };

  const generateReport = async () => {
    if (!sessionId) return;
    setStatus('generating');
    try {
      const response = await fetch(`${API}/api/import/${sessionId}/generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeader() },
        body: JSON.stringify({ output_type: outputType }),
      });
      const data = await readResponse(response, 'Failed to generate the report');
      setStatus('ready');
      const isDashboard = data.output_type === 'dashboard';
      toast.success(isDashboard ? 'Dashboard generated! Opening in the builder…' : 'Report configuration generated! Opening builder...');
      sessionStorage.setItem('importedReportConfig', JSON.stringify(data.report_config));
      sessionStorage.setItem('importedModelId', selectedModelId);
      if (sessionId) sessionStorage.setItem('importedSessionId', sessionId);
      onOpenChange(false);
      navigate('/dashboards/builder?mode=report&imported=true');
    } catch (err) {
      setStatus('error'); setError(err.message); toast.error(err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetState(); onOpenChange(isOpen); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="import-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> Create from File (AI)</DialogTitle>
          <DialogDescription>Upload a screenshot, PDF, or Excel file of an existing report. AI will analyze it and recreate it as a report or dashboard.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 flex flex-col overflow-hidden">
          {status === 'idle' && <ImportUploadZone onFile={handleFile} />}
          {status === 'model_select' && (
            <ImportModelSelect
              fileName={fileName} models={models} modelsLoading={modelsLoading}
              selectedModelId={selectedModelId} setSelectedModelId={setSelectedModelId}
              outputType={outputType} setOutputType={setOutputType}
              onBack={() => { setStatus('idle'); setPendingFile(null); setFileName(''); }}
              onProceed={proceedWithUpload}
            />
          )}
          {status === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <div className="text-center"><p className="font-medium">Uploading and analyzing...</p><p className="text-sm text-muted-foreground">{fileName}</p></div>
              <div className="w-64 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary transition-all duration-300" style={{ width: `${uploadProgress}%` }} /></div>
            </div>
          )}
          {(status === 'analyzed' || status === 'chatting' || status === 'generating') && (
            <ImportChatView
              fileName={fileName} pageCount={pageCount} analysis={analysis}
              messages={messages} inputMessage={inputMessage} setInputMessage={setInputMessage}
              sending={sending} status={status}
              onSendMessage={sendMessage} onGenerateReport={generateReport} onReset={resetState}
            />
          )}
          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center"><XCircle className="w-8 h-8 text-destructive" /></div>
              <div className="text-center"><p className="font-medium text-destructive">Analysis Failed</p><p className="text-sm text-muted-foreground mt-1">{error}</p></div>
              <Button onClick={resetState}>Try Again</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
